import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../../components/TanTable';
import BillToLookupField, { type BillToValue } from '../../../components/BillToLookupField';
import LookupSearchField from '../../../components/tablemaint/LookupSearchField';
import LookupResultCard from '../../order-management/components/LookupResultCard';
import quoteService from '../../../services/quote.service';
import type { CustomerLookupRow, LocationLookupRow } from '../../order-management/fixtures';
import ordersService, {
  type ChangeTemplateCandidate,
  type ChangeTemplatePreviewRow,
  type ChangeTemplateApplyResult,
} from '../../../services/orders.service';
import { groupLegChanges } from '../../../utils/changeTemplateLegGroups';
import {
  useAdditionalFieldColumns,
  useAdditionalFieldSelection,
} from '../../../hooks/useAdditionalFields';

interface CodeName {
  recno: number;
  name: string;
}

interface Props {
  isOpen: boolean;
  /** The pros to bulk-change — the caller passes the CURRENTLY VISIBLE / filtered
   *  set from the grid (never an implicit "all"), mirroring the Send All actions. */
  pros: (number | string)[];
  onClose: () => void;
  /** Called after an apply run so the grid can refetch. */
  onApplied?: () => void;
}

const s = (v: unknown): string => (v == null ? '' : String(v));

/** Candidate grid columns — same focused slice as the single-pro modal.
 *  `pronumber` opts out of registry titling for the same reason it does there: the
 *  registry calls the field "Pro No.", and these rows are TEMPLATES. De-duplication
 *  is unaffected, so it is still not offered twice in the column picker. */
const COLUMNS: ColumnDef<ChangeTemplateCandidate>[] = [
  { accessorKey: 'pronumber', header: 'Template #', meta: { registryTitle: false }, cell: (c) => s(c.getValue()) },
  { accessorKey: 'movetype', header: 'Move', cell: (c) => s(c.getValue()) },
  { accessorKey: 'ratezone', header: 'Rate Zone', cell: (c) => s(c.getValue()) },
  { accessorKey: 'size', header: 'Size', cell: (c) => s(c.getValue()) },
  { accessorKey: 'equiptype', header: 'Type', cell: (c) => s(c.getValue()) },
  { accessorKey: 'delivery', header: 'Delivery', cell: (c) => s(c.getValue()) },
  { accessorKey: 'quotenumber', header: 'Quote #', cell: (c) => s(c.getValue()) },
  { accessorKey: 'csr', header: 'CSR', cell: (c) => s(c.getValue()) },
  { accessorKey: 'approvedby', header: 'Approved By', cell: (c) => s(c.getValue()) },
];

/** Namespaces this grid's saved layout AND its added-column selection — shared by
 *  every page that mounts this modal (Search Results, the LC and Dispatch
 *  dashboards), since it is one dialog wherever it is opened from. */
const CANDIDATE_GRID_KEY = 'change-template-bulk-candidates';

/**
 * Change Template for All — bulk variant of the /pro Change Template flow.
 *
 * Applies ONE approved template to a list of pros (the grid's visible/filtered set),
 * mirroring the "Send All Haz Docs / Send All Rail Bill" mass actions. Three steps:
 *   1. search — pick the target template (bill-to / customer / delivery lookups + grid).
 *   2. review — per-pro eligibility summary (X eligible / Y can't change, with reasons)
 *               + the optional fields to apply across all pros.
 *   3. results — the applied / skipped breakdown returned by the apply run.
 *
 * The backend re-validates every pro and applies each in its own transaction, so an
 * ineligible or failing pro is skipped (with a reason) without aborting the batch.
 */
const ChangeTemplateBulkModal: React.FC<Props> = ({ isOpen, pros, onClose, onApplied }) => {
  const [step, setStep] = useState<'search' | 'review' | 'results'>('search');
  const [billTo, setBillTo] = useState<BillToValue>({ recno: 0, name: '' });
  const [customer, setCustomer] = useState<CodeName>({ recno: 0, name: '' });
  const [deliverTo, setDeliverTo] = useState<CodeName>({ recno: 0, name: '' });
  const [rows, setRows] = useState<ChangeTemplateCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [capped, setCapped] = useState(false);
  const [selected, setSelected] = useState<ChangeTemplateCandidate | null>(null);

  const [preview, setPreview] = useState<ChangeTemplatePreviewRow[] | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [checkedOptional, setCheckedOptional] = useState<Set<string>>(new Set());

  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [results, setResults] = useState<ChangeTemplateApplyResult[] | null>(null);
  // Live progress across the chunked apply (done/total pros) for the progress bar.
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  // Synchronous re-entrancy guard (a double-click must not fire two apply runs).
  const applyingRef = useRef(false);

  // The modal stays mounted across open/close (the parent toggles isOpen), so its
  // state must be reset on close — otherwise reopening for a NEW set of pros would
  // show the PREVIOUS run's results/selection. Reset when closed so the next open
  // starts fresh at the search step.
  useEffect(() => {
    if (isOpen) return;
    setStep('search');
    setBillTo({ recno: 0, name: '' });
    setCustomer({ recno: 0, name: '' });
    setDeliverTo({ recno: 0, name: '' });
    setRows([]);
    setCapped(false);
    setSelected(null);
    setPreview(null);
    setCheckedOptional(new Set());
    setResults(null);
    setApplyError(null);
    setApplying(false);
    setProgress(null);
    applyingRef.current = false;
  }, [isOpen]);

  const canSearch = billTo.recno > 0 && customer.recno > 0;

  // Any orders-scope registry field can be added as a column, resolved against the
  // TEMPLATE tables — the same picker the Templates board and the single-pro Change
  // Template modal offer. Keyed on `isOpen`: this modal stays MOUNTED behind its host
  // page, and a null key means "no grid to remember for", so a page that never opens
  // it doesn't pay a settings round-trip on load.
  const fieldSelection = useAdditionalFieldSelection(
    isOpen ? CANDIDATE_GRID_KEY : null, { binding: 'templates' },
  );
  const additionalFieldsKey = fieldSelection.additionalFields.join(',');
  const { columns, availableFields } = useAdditionalFieldColumns<ChangeTemplateCandidate>(
    fieldSelection, { baseColumns: COLUMNS, binding: 'templates' },
  );

  // Live candidate search once both bill-to + customer are set (same gate as single-pro).
  useEffect(() => {
    // Holding on `ready` keeps the first search from firing WITHOUT the saved added
    // columns and then again with them.
    if (!isOpen || step !== 'search' || !canSearch || !fieldSelection.ready) {
      setRows([]);
      return;
    }
    const ctrl = new AbortController();
    setLoading(true);
    const timer = setTimeout(() => {
      ordersService
        .changeTemplateCandidates(
          {
            billto: billTo.recno,
            customer: customer.recno,
            deliverto: deliverTo.recno || undefined,
            additionalFields: fieldSelection.additionalFields,
          },
          ctrl.signal,
        )
        .then((res) => {
          setRows(res.rows);
          setCapped(res.capped);
        })
        .catch(() => setRows([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
    // `additionalFieldsKey` (not the array) so a re-render with an equal selection
    // doesn't re-run the search; adding or removing a column does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, step, canSearch, billTo.recno, customer.recno, deliverTo.recno,
      fieldSelection.ready, additionalFieldsKey]);

  // On entering review, preview all pros against the picked template.
  useEffect(() => {
    if (step !== 'review' || !selected) return;
    const ctrl = new AbortController();
    setPreviewLoading(true);
    setPreview(null);
    setApplyError(null);
    ordersService
      .changeTemplatePreview({ template: selected.pronumber, pros }, ctrl.signal)
      .then((res) => {
        setPreview(res.rows);
        // Default-check every optional field that differs on any eligible pro.
        const opt = new Set<string>();
        res.rows.forEach((r) => {
          if (r.eligible) r.diffs.forEach((d) => d.optional && opt.add(d.field));
        });
        setCheckedOptional(opt);
      })
      .catch(() => setPreview([]))
      .finally(() => setPreviewLoading(false));
    return () => ctrl.abort();
  }, [step, selected, pros]);

  const eligible = useMemo(() => (preview ?? []).filter((r) => r.eligible), [preview]);
  const skipped = useMemo(() => (preview ?? []).filter((r) => !r.eligible), [preview]);
  const legGroups = useMemo(() => groupLegChanges(eligible), [eligible]);

  // Distinct optional fields (field -> label) across the eligible pros' diffs.
  const optionalFields = useMemo(() => {
    const m = new Map<string, string>();
    eligible.forEach((r) => r.diffs.forEach((d) => d.optional && m.set(d.field, d.label)));
    return Array.from(m, ([field, label]) => ({ field, label }));
  }, [eligible]);

  // Apply in small chunks so the modal can show live progress (X of N). Only the
  // preview-eligible pros are sent (ineligible ones were already shown as skipped).
  // A modest chunk keeps the server's per-batch template read + shared deps mostly
  // intact while still updating the bar frequently.
  const APPLY_CHUNK = 4;

  const handleApply = async () => {
    if (applyingRef.current || !selected || eligible.length === 0) return;
    applyingRef.current = true;
    setApplying(true);
    setApplyError(null);
    const eligiblePros = eligible.map((r) => String(r.pronumber));
    const total = eligiblePros.length;
    setProgress({ done: 0, total });
    // One nonce for the whole run — the (nonce, pronumber) idempotency key makes it
    // safe across chunks (each pro is in exactly one chunk).
    const nonce =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `ctb-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const acc: ChangeTemplateApplyResult[] = [];
    let errored: string | null = null;
    for (let i = 0; i < total; i += APPLY_CHUNK) {
      const chunk = eligiblePros.slice(i, i + APPLY_CHUNK);
      try {
        const res = await ordersService.changeTemplateApply({
          template: selected.pronumber,
          pros: chunk,
          selectedOptional: Array.from(checkedOptional),
          submitNonce: nonce,
        });
        acc.push(...(res.results || []));
      } catch (e: unknown) {
        const detail =
          (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
        errored = detail || 'Could not apply the template change.';
        break;
      }
      setProgress({ done: Math.min(i + APPLY_CHUNK, total), total });
    }

    applyingRef.current = false;
    setApplying(false);
    setProgress(null);
    // Nothing applied + errored -> stay on review with the error. Otherwise show the
    // results (noting a partial error if the run stopped early).
    if (errored && acc.length === 0) {
      setApplyError(errored);
      return;
    }
    if (errored) setApplyError(errored);
    setResults(acc);
    setStep('results');
    onApplied?.();
  };

  // Alt+C closes (never mid-apply). Applying a template across the selected PROs
  // gets no chord — same reasoning as the other bulk review modals.
  useAltHotkeys({ C: applying ? null : onClose }, undefined, { layer: isOpen });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(isOpen);

  if (!isOpen) return null;

  const handleClose = () => {
    if (applyingRef.current) return;
    onClose();
  };

  const applied = (results ?? []).filter((r) => r.applied);
  const notApplied = (results ?? []).filter((r) => !r.applied);

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-scrim/40 p-4"
      onMouseDown={handleClose}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="bg-panel rounded-lg shadow-xl w-[min(1040px,96vw)] max-h-[90vh] flex flex-col"
        role="dialog"
        aria-label="Change Template for All"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <ModalHeader
          title={`Change Template for All — ${pros.length} pro${pros.length === 1 ? '' : 's'}`}
          onClose={handleClose}
          dragHandleProps={dragHandleProps}
          className="shrink-0"
        />

        {step === 'search' && (
          <div className="p-4 flex flex-col gap-4 min-h-0">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <BillToLookupField value={billTo} onChange={setBillTo} required />
              <div>
                <LookupSearchField<CustomerLookupRow>
                  label="Customer"
                  required
                  displayValue={customer.name}
                  onType={(t) => setCustomer({ recno: 0, name: t })}
                  onSelect={(r) => setCustomer({ recno: r.customercode, name: r.custname })}
                  search={(q) => quoteService.lookupCustomer(q)}
                  renderItem={(r) => (
                    <LookupResultCard
                      name={r.custname}
                      code={r.customercode}
                      address1={r.address1}
                      address2={r.address2}
                      city={r.city}
                      state={r.state}
                      zip={r.zip}
                    />
                  )}
                  itemValue={(r) => String(r.customercode)}
                  itemLabel={(r) => r.custname}
                  placeholder="Search customers by name or #…"
                />
                <div className="mt-1.5 text-xs">
                  {customer.recno > 0 ? (
                    <span className="text-green-700 dark:text-green-400">#{customer.recno}</span>
                  ) : (
                    <span className="text-muted">No Customer selected</span>
                  )}
                </div>
              </div>
              <div>
                <LookupSearchField<LocationLookupRow>
                  label="Delivery Location"
                  displayValue={deliverTo.name}
                  onType={(t) => setDeliverTo({ recno: 0, name: t })}
                  onSelect={(r) => setDeliverTo({ recno: r.locationcode, name: r.lname })}
                  search={(q) => quoteService.lookupLocation(q)}
                  renderItem={(r) => (
                    <LookupResultCard
                      name={r.lname}
                      code={r.locationcode}
                      city={r.city}
                      state={r.state}
                      zip={r.zip}
                    />
                  )}
                  itemValue={(r) => String(r.locationcode)}
                  itemLabel={(r) => r.lname}
                  placeholder="Optional — narrow by delivery location…"
                />
                <div className="mt-1.5 text-xs text-muted">
                  {deliverTo.recno > 0 ? `#${deliverTo.recno}` : 'Any delivery location'}
                </div>
              </div>
            </div>

            <div className="flex-1 min-h-[320px] overflow-hidden flex flex-col">
              {!canSearch ? (
                <div className="flex-1 flex items-center justify-center text-sm text-slate-500 dark:text-gray-400">
                  Select a Bill To and Customer to find matching approved templates.
                </div>
              ) : (
                <>
                  <TanTable<ChangeTemplateCandidate>
                    data={rows}
                    columns={columns}
                    settingsKey={CANDIDATE_GRID_KEY}
                    availableFields={availableFields}
                    additionalFields={fieldSelection.additionalFields}
                    onAdditionalFieldsChange={fieldSelection.onAdditionalFieldsChange}
                    loading={loading}
                    enableSearch
                    searchPlaceholder="Filter templates…"
                    enablePagination={false}
                    getRowId={(r) => String(r.pronumber)}
                    onRowClick={(r) => {
                      setSelected(r);
                      setStep('review');
                    }}
                  />
                  {capped && (
                    <div className="mt-1 text-[0.6875rem] text-amber-600 dark:text-amber-400">
                      Showing the first results — narrow the search to see more.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {step === 'review' && (
          <div className="p-4 flex flex-col gap-3 min-h-0 overflow-auto">
            <div className="flex items-center justify-between shrink-0">
              <h4 className="text-sm font-semibold text-slate-800 dark:text-gray-100">
                Apply Template <span>{s(selected?.pronumber)}</span> to{' '}
                {pros.length} pro{pros.length === 1 ? '' : 's'}
              </h4>
              <button
                type="button"
                onClick={() => setStep('search')}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
              >
                ← Pick a different template
              </button>
            </div>

            {previewLoading ? (
              <div className="py-10 text-center text-sm text-slate-500 dark:text-gray-400">
                Checking {pros.length} pros…
              </div>
            ) : (
              <>
                {/* Eligibility summary */}
                <div className="grid grid-cols-2 gap-3 shrink-0">
                  <div className="rounded border border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-900/20 px-3 py-2">
                    <div className="text-lg font-semibold text-green-700 dark:text-green-300">
                      {eligible.length}
                    </div>
                    <div className="text-xs text-green-700 dark:text-green-400">will be changed</div>
                  </div>
                  <div className="rounded border border-slate-300 dark:border-gray-700 bg-slate-50 dark:bg-gray-900/30 px-3 py-2">
                    <div className="text-lg font-semibold text-slate-600 dark:text-gray-300">
                      {skipped.length}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-gray-400">cannot be changed</div>
                  </div>
                </div>

                {/* Will-be-changed pros, comma-delimited (mirrors the results "Changed:" list) */}
                {eligible.length > 0 && (
                  <div className="text-xs text-slate-600 dark:text-gray-300 shrink-0">
                    <span className="font-medium">Will be changed:</span>{' '}
                    <span className="break-words">
                      {eligible.map((r) => String(r.pronumber)).join(', ')}
                    </span>
                  </div>
                )}

                {/* What the apply does to each pro's legs (bugfix/#34227) — a template
                    that adds a chassis split, or one that can't because a driver is
                    assigned, was otherwise invisible here. */}
                {legGroups.length > 0 && (
                  <div className="shrink-0 rounded border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
                    <div className="font-medium mb-1">Legs:</div>
                    <ul className="flex flex-col gap-1.5">
                      {legGroups.map((g) => (
                        <li key={g.label}>
                          <div>
                            <span className="font-medium">{g.label}</span>
                            {' — '}
                            {g.pros.length} pro{g.pros.length === 1 ? '' : 's'}
                          </div>
                          <div className="break-words opacity-80">{g.pros.join(', ')}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Optional fields applied to all */}
                {optionalFields.length > 0 && (
                  <div className="shrink-0">
                    <div className="text-xs font-medium text-slate-600 dark:text-gray-300 mb-1">
                      Optional fields to apply (to every changed pro):
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {optionalFields.map((o) => (
                        <label key={o.field} className="inline-flex items-center gap-1.5 text-xs text-slate-700 dark:text-gray-200">
                          <input
                            type="checkbox"
                            checked={checkedOptional.has(o.field)}
                            onChange={(e) =>
                              setCheckedOptional((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(o.field);
                                else next.delete(o.field);
                                return next;
                              })
                            }
                          />
                          {o.label}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Skipped pros + reasons */}
                {skipped.length > 0 && (
                  <div className="min-h-0 overflow-auto border border-slate-200 dark:border-gray-700 rounded">
                    <table className="w-full text-[0.75rem]">
                      <thead className="bg-slate-100 dark:bg-gray-700/50 text-slate-600 dark:text-gray-300">
                        <tr>
                          <th className="text-left font-medium px-2 py-1.5 w-28">Pro</th>
                          <th className="text-left font-medium px-2 py-1.5">Cannot change — reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {skipped.map((r) => (
                          <tr key={String(r.pronumber)} className="border-t border-slate-100 dark:border-gray-700">
                            <td className="px-2 py-1 text-slate-700 dark:text-gray-200">{String(r.pronumber)}</td>
                            <td className="px-2 py-1 text-slate-500 dark:text-gray-400">{r.errors.join('; ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {applyError && (
                  <div className="rounded border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-xs text-red-700 dark:text-red-300 shrink-0">
                    {applyError}
                  </div>
                )}
              </>
            )}

            {applying && progress && (
              <div className="shrink-0">
                <div className="flex items-center justify-between text-xs text-slate-600 dark:text-gray-300 mb-1">
                  <span>Applying template to pros…</span>
                  <span className="tabular-nums">{progress.done} of {progress.total}</span>
                </div>
                <div className="h-2 rounded bg-slate-200 dark:bg-gray-700 overflow-hidden">
                  <div
                    className="h-full bg-blue-600 transition-all duration-300"
                    style={{ width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%` }}
                  />
                </div>
                <div className="mt-1 text-[0.6875rem] text-slate-400 dark:text-gray-500">
                  Please keep this window open until it finishes.
                </div>
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-1 shrink-0">
              <button
                type="button"
                onClick={() => setStep('search')}
                disabled={applying}
                className="px-3 py-1.5 rounded border border-slate-300 dark:border-gray-600 text-sm hover:bg-slate-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleApply}
                disabled={applying || previewLoading || eligible.length === 0}
                title={eligible.length === 0 ? 'No eligible pros to change' : 'Apply the template'}
                className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {applying
                  ? (progress ? `Applying… ${progress.done}/${progress.total}` : 'Applying…')
                  : `Apply to ${eligible.length} pro${eligible.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        )}

        {step === 'results' && (
          <div className="p-4 flex flex-col gap-3 min-h-0 overflow-auto">
            <div className="text-sm text-slate-700 dark:text-gray-200 shrink-0">
              Template <span>{s(selected?.pronumber)}</span> applied.
            </div>
            {applyError && (
              <div className="rounded border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-xs text-red-700 dark:text-red-300 shrink-0">
                The run stopped early: {applyError} — some pros below may not have been processed.
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 shrink-0">
              <div className="rounded border border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-900/20 px-3 py-2 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" />
                <span className="text-sm text-green-700 dark:text-green-300">
                  <span className="font-semibold">{applied.length}</span> changed
                </span>
              </div>
              <div className="rounded border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
                <span className="text-sm text-amber-700 dark:text-amber-300">
                  <span className="font-semibold">{notApplied.length}</span> skipped
                </span>
              </div>
            </div>

            {applied.length > 0 && (
              <div className="text-xs text-slate-600 dark:text-gray-300 shrink-0">
                <span className="font-medium">Changed:</span>{' '}
                <span>{applied.map((r) => String(r.pronumber)).join(', ')}</span>
              </div>
            )}

            {notApplied.length > 0 && (
              <div className="min-h-0 overflow-auto border border-slate-200 dark:border-gray-700 rounded">
                <table className="w-full text-[0.75rem]">
                  <thead className="bg-slate-100 dark:bg-gray-700/50 text-slate-600 dark:text-gray-300">
                    <tr>
                      <th className="text-left font-medium px-2 py-1.5 w-28">Pro</th>
                      <th className="text-left font-medium px-2 py-1.5">Skipped — reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notApplied.map((r) => (
                      <tr key={String(r.pronumber)} className="border-t border-slate-100 dark:border-gray-700">
                        <td className="px-2 py-1 text-slate-700 dark:text-gray-200">{String(r.pronumber)}</td>
                        <td className="px-2 py-1 text-slate-500 dark:text-gray-400">{r.errors.join('; ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex items-center justify-end pt-1 shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm hover:bg-blue-700"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default ChangeTemplateBulkModal;
